/**
 * Context menu commands (requirement 12.13).
 *
 * What each element's menu offers, built from editor state so every item
 * dispatches a real reducer action, or asks the host for an operation that
 * involves the server (rename, blocks, findings). Commands that do not apply
 * are listed disabled rather than hidden, so the menu has the same shape every
 * time; an operation the host has not provided is disabled the same way.
 *
 * Commands only: no headings, no explanations. The reason a command is
 * disabled is kept on the entry for assistive technology and tests, and not
 * displayed.
 */
import { LABEL_SIDES, servesKind, type EdgeType, type LabelSide } from "../design/document";
import type { Point, Scene } from "../scene/types";
import type { MenuEntry, MenuItem } from "./ContextMenu";
import { checkEndpoint, checkTemplateSwap, connectionAt, findPort, lockingInstance, nextFreePort } from "./rules";
import type { NoteEditorHandle } from "./NoteEditor";
import type { EditorAction, EditorState } from "./state";
import { nearestComponent, type Target, type ViewCommand } from "./interaction";
import { isMountable } from "./mounts";

/**
 * Operations the editor cannot perform on its own document: they involve
 * lineage, the library or another view, so they are server endpoints or
 * other panels (design §1.3). Each is optional; a missing one disables its
 * command.
 */
export interface EditorHost {
  rename?: (ref: string) => void;
  inspect?: (ref: string) => void;
  saveAsBlock?: (refs: string[]) => void;
  detach?: (instance: string) => void;
  openDefinition?: (instance: string) => void;
  updateBlock?: (instance: string) => void;
  /** Whether a newer definition version exists; the update command needs one. */
  hasNewerVersion?: (instance: string) => boolean;
  revealFindings?: (ref: string) => void;
  /**
   * The latest templates a component could change to: same role, this kind
   * of design, not the one it has. Named, for the menu.
   */
  swapCandidates?: (ref: string) => { versionId: string; name: string }[];
  /** Place an instance of a block definition version with its centre at `at`. */
  placeBlock?: (definitionVersionId: string, at: Point) => void;
}

export interface CommandContext {
  state: EditorState;
  scene: Scene;
  host: EditorHost;
  /** Whether this canvas takes notes (a design's does; a block definition's does not). */
  notes?: boolean;
  /** The note being written, for its text controls. */
  noteText?: NoteEditorHandle;
  dispatch: (action: EditorAction) => void;
  view: (command: ViewCommand) => void;
  /** Start a connection from a port without a pointer drag (menu and keyboard). */
  connectFrom: (port: string) => void;
  /** Where the menu was opened, in scene units: where Paste puts things. */
  at?: Point;
}

/** Cut, copy and paste, for the selection's menus and the canvas's. */
function clipboardCommands(context: CommandContext, locked?: string): MenuEntry[] {
  const { state, dispatch } = context;
  return [
    item("Cut", locked ? undefined : () => dispatch({ type: "CUT_SELECTION" }), {
      shortcut: "⌘X",
      ...(locked ? { disabledReason: locked } : {}),
    }),
    item("Copy", () => dispatch({ type: "COPY_SELECTION" }), { shortcut: "⌘C" }),
    pasteCommand(context, state.clipboard !== null),
  ];
}

function pasteCommand(context: CommandContext, available: boolean): MenuItem {
  return item("Paste", available ? () => context.dispatch({ type: "PASTE", at: context.at }) : undefined, {
    shortcut: "⌘V",
    ...(available ? {} : { disabledReason: "Nothing copied yet" }),
  });
}

function item(label: string, onSelect: (() => void) | undefined, options: Partial<MenuItem> = {}): MenuItem {
  return {
    label,
    ...(onSelect ? { onSelect } : { disabledReason: options.disabledReason ?? "Not available here" }),
    ...options,
  };
}

function hostItem<T>(
  label: string,
  operation: ((argument: T) => void) | undefined,
  argument: T,
  options: Partial<MenuItem> = {},
): MenuItem {
  return operation
    ? item(label, () => operation(argument), options)
    : item(label, undefined, { disabledReason: "Not available yet", ...options });
}

const LINE_KINDS: readonly { role: EdgeType; label: string }[] = [
  { role: "channel", label: "Straight channel" },
  { role: "mixing_channel", label: "Mixing channel" },
  { role: "air_line", label: "Air line" },
];

/** The template a line of this kind is made from: the editor's own choice first. */
function templateFor(state: EditorState, role: EdgeType): string | undefined {
  const preferred = [state.connectorTemplateId, state.airLineTemplateId].find((id) => {
    const template = id ? state.library.templates[id] : undefined;
    return template?.role === role && servesKind(template, state.designKind);
  });
  return (
    preferred ??
    Object.entries(state.library.templates).find(
      ([, template]) => template.element === "edge" && template.role === role && servesKind(template, state.designKind),
    )?.[0]
  );
}

/**
 * "Change to …" for each other template of the component's role, so a sensor
 * becomes another kind of sensor in place. One that would strand a line is
 * shown disabled with the reason.
 */
function swapCommands(context: CommandContext, ref: string): MenuItem[] {
  const candidates = context.host.swapCandidates?.(ref) ?? [];
  return candidates.map((candidate) => {
    const refusal = checkTemplateSwap(context.state.document, context.state.library, ref, candidate.versionId);
    return item(
      candidate.name,
      refusal ? undefined : () => context.dispatch({ type: "SET_NODE_TEMPLATE", ref, templateVersionId: candidate.versionId }),
      refusal ? { disabledReason: refusal.message } : {},
    );
  });
}

/**
 * Every "change to" choice under one item, opening beside it, so a menu with
 * several alternatives stays short. Nothing when there is nothing to change to.
 */
function changeType(choices: MenuItem[], lockedReason?: string): MenuItem[] {
  if (choices.length === 0) return [];
  if (lockedReason) return [item("Change type", undefined, { disabledReason: lockedReason })];
  return [{ label: "Change type", submenu: choices }];
}

/** Whether a component drawn in line sits on its tube and could be mounted on one (D40). */
function mountableHere(state: EditorState, ref: string): boolean {
  const node = state.document.nodes?.find((candidate) => candidate.local_id === ref);
  if (!node || node.mount) return false;
  return isMountable(node, node.template_version_id ? state.library.templates[node.template_version_id] : undefined);
}

/** Where "Move label" sends a label next: clockwise from below. */
function nextSideName(side: LabelSide): string {
  const next = LABEL_SIDES[(LABEL_SIDES.indexOf(side) + 1) % LABEL_SIDES.length]!;
  return next === "below" ? "below" : next === "above" ? "above" : `to the ${next}`;
}

function orientCommands(dispatch: CommandContext["dispatch"]): MenuEntry[] {
  return [
    item("Rotate 90° clockwise", () => dispatch({ type: "ROTATE_SELECTION", quarterTurns: 1 }), { shortcut: "R" }),
    item("Rotate 90° anticlockwise", () => dispatch({ type: "ROTATE_SELECTION", quarterTurns: -1 }), { shortcut: "⇧R" }),
    item("Rotate 180°", () => dispatch({ type: "ROTATE_SELECTION", quarterTurns: 2 })),
    item("Flip horizontal", () => dispatch({ type: "MIRROR_SELECTION", axis: "horizontal" }), { shortcut: "H" }),
    item("Flip vertical", () => dispatch({ type: "MIRROR_SELECTION", axis: "vertical" }), { shortcut: "⇧H" }),
  ];
}

function findingsCommand(context: CommandContext, ref: string, count: number): MenuEntry[] {
  if (count === 0) return [];
  const label = `Show ${count} finding${count === 1 ? "" : "s"}`;
  return [hostItem(label, context.host.revealFindings, ref)];
}

/**
 * The menu for a target. The selection has already been adjusted by the
 * canvas — right-clicking outside the selection selects what was hit — so
 * commands that act on "the selection" act on what the designer sees.
 */
export function commandsFor(target: Target, context: CommandContext): MenuEntry[] {
  const { state, scene, host, dispatch } = context;
  const design = state.document;
  const several = state.selection.size > 1;

  if (target.kind === "note-text") {
    const editor = context.noteText;
    const run = (command: Parameters<NoteEditorHandle["format"]>[0]) =>
      editor ? () => editor.format(command) : undefined;
    return [
      item("Bold", run("bold"), { shortcut: "⌘B" }),
      item("Italic", run("italic"), { shortcut: "⌘I" }),
      item("Underline", run("underline"), { shortcut: "⌘U" }),
      item("Strikethrough", run("strikeThrough"), { shortcut: "⇧⌘X" }),
      "separator",
      item("Bulleted list", run("insertUnorderedList"), { shortcut: "⇧⌘8" }),
      item("Numbered list", run("insertOrderedList"), { shortcut: "⇧⌘7" }),
      "separator",
      item("Done", editor ? () => editor.commit() : undefined, { shortcut: "⌘↵" }),
    ];
  }

  if (target.kind === "note" && target.ref) {
    const ref = target.ref;
    const note = design.text_notes?.find((candidate) => candidate.local_id === ref);
    const nearest = nearestComponent(scene, ref);
    return [
      item("Edit note", several ? undefined : () => context.view({ kind: "edit-label" }), {
        shortcut: "F2",
        ...(several ? { disabledReason: "Select one note to edit it" } : {}),
      }),
      note?.anchor
        ? item(`Detach from ${note.anchor}`, () => dispatch({ type: "SET_NOTE_ANCHOR", ref, anchor: null }))
        : item(
            nearest ? `Attach to ${nearest}` : "Attach to nearest component",
            nearest ? () => dispatch({ type: "SET_NOTE_ANCHOR", ref, anchor: nearest }) : undefined,
            nearest ? {} : { disabledReason: "No component near enough" },
          ),
      ...(note && !several
        ? [
            {
              label: "Resize",
              submenu: (() => {
                const drawn = scene.notes.find((candidate) => candidate.ref === ref);
                const size = drawn?.size ?? note.size ?? { width: 200, height: 40 };
                const step = state.grid.spacing > 0 ? state.grid.spacing : 16;
                const resize = (dw: number, dh: number) => () =>
                  dispatch({ type: "SET_NOTE_SIZE", ref, size: { width: size.width + dw, height: size.height + dh } });
                return [
                  item("Wider", resize(step, 0)),
                  item("Narrower", resize(-step, 0)),
                  item("Taller", resize(0, step)),
                  item("Shorter", resize(0, -step)),
                ];
              })(),
            } satisfies MenuItem,
          ]
        : []),
      "separator",
      ...clipboardCommands(context),
      "separator",
      item(several ? `Delete ${state.selection.size} elements` : "Delete note", () => dispatch({ type: "DELETE_SELECTION" }), {
        shortcut: "Del",
        danger: true,
      }),
    ];
  }

  if (target.kind === "node" && target.ref) {
    const ref = target.ref;
    const node = scene.nodes.find((candidate) => candidate.ref === ref);
    const instance = lockingInstance(design, ref);
    const locked = instance ? { disabledReason: `Locked in ${instance.local_id}` } : {};
    return [
      ...orientCommands(dispatch),
      "separator",
      ...(several
        ? []
        : [
            item("Edit label", () => context.view({ kind: "edit-label" }), { shortcut: "F2" }),
            item(`Move label ${nextSideName(node?.labelSide ?? "below")}`, () => dispatch({ type: "SET_LABEL_SIDE" }), {
              shortcut: "L",
            }),
            hostItem("Rename identifier…", host.rename, ref),
            hostItem("Edit parameters", host.inspect, ref, { shortcut: "↵" }),
            ...(context.notes ? [item("Add note", () => context.view({ kind: "add-note", anchor: ref }))] : []),
            ...(state.designKind === "tubing_set" && mountableHere(state, ref)
              ? [item("Mount on line", () => dispatch({ type: "MOUNT_ON_LINE", ref }))]
              : []),
            ...changeType(swapCommands(context, ref), instance ? `Locked in ${instance.local_id}` : undefined),
          ]),
      hostItem(several ? "Save selection as block…" : "Save as block…", host.saveAsBlock, [...state.selection], locked),
      "separator",
      ...clipboardCommands(context, instance ? `Locked in ${instance.local_id}` : undefined),
      "separator",
      ...findingsCommand(context, ref, node?.findings.length ?? 0),
      ...(instance ? [hostItem(`Detach ${instance.local_id}…`, host.detach, instance.local_id)] : []),
      item(several ? `Delete ${state.selection.size} elements` : "Delete", () => dispatch({ type: "DELETE_SELECTION" }), {
        shortcut: "Del",
        danger: true,
        ...(instance ? { onSelect: undefined, disabledReason: `Locked in ${instance.local_id}` } : {}),
      }),
    ];
  }

  if (target.kind === "edge" && target.ref) {
    const ref = target.ref;
    const edge = design.edges?.find((candidate) => candidate.local_id === ref);
    const sceneEdge = scene.edges.find((candidate) => candidate.ref === ref);
    const instance = lockingInstance(design, ref);
    const lockedReason = instance ? `Locked in ${instance.local_id}` : undefined;
    // One command per other kind of line, keeping the line's dimensions. Only
    // kinds this design has a template for: a tubing set has no mixing
    // channels, so it is not offered one (D34).
    const current = edge?.type ?? "channel";
    const changes = LINE_KINDS.filter(
      (kind) => kind.role !== current && templateFor(state, kind.role) !== undefined,
    ).map((kind) => {
      const template = templateFor(state, kind.role);
      // A tubing set's lines are tubes (D34).
      const label =
        state.designKind === "tubing_set" ? (kind.role === "air_line" ? "Air tubing" : "Tubing") : kind.label;
      return template && !lockedReason
        ? item(label, () => dispatch({ type: "SET_EDGE_TEMPLATE", ref, templateVersionId: template }))
        : item(label, undefined, { disabledReason: lockedReason ?? "No such template in the library" });
    });
    return [
      hostItem("Edit hydraulic length…", host.inspect, ref, lockedReason ? { disabledReason: lockedReason } : {}),
      ...changeType(changes, lockedReason),
      item("Insert junction", lockedReason ? undefined : () => dispatch({ type: "BRANCH_EDGE", edge: ref }), {
        shortcut: "J",
        ...(lockedReason ? { disabledReason: lockedReason } : {}),
      }),
      hostItem("Rename identifier…", host.rename, ref),
      // Bends are presentation, so this stays available on locked members.
      item(
        "Reset to automatic route",
        sceneEdge?.manual ? () => dispatch({ type: "SET_EDGE_WAYPOINTS", ref, waypoints: [] }) : undefined,
        sceneEdge?.manual ? {} : { disabledReason: "Already routed automatically" },
      ),
      "separator",
      ...findingsCommand(context, ref, sceneEdge?.findings.length ?? 0),
      item("Delete", lockedReason ? undefined : () => dispatch({ type: "DELETE_SELECTION" }), {
        shortcut: "Del",
        danger: true,
        ...(lockedReason ? { disabledReason: lockedReason } : {}),
      }),
    ];
  }

  if (target.kind === "block" && target.ref) {
    const ref = target.ref;
    const block = scene.blocks.find((candidate) => candidate.ref === ref);
    const collapsed = state.collapsed.has(ref);
    const newer = host.hasNewerVersion?.(ref) ?? false;
    return [
      item(collapsed ? "Expand" : "Collapse", () => dispatch({ type: "SET_COLLAPSED", instance: ref, collapsed: !collapsed })),
      ...(collapsed
        ? [
            design.group_instances?.find((instance) => instance.local_id === ref)?.collapsed_position
              ? item("Place on its lines", () => dispatch({ type: "SET_COLLAPSED_POSITION", instance: ref, position: null }))
              : item("Place on its lines", undefined, { disabledReason: "Already placed from its lines" }),
          ]
        : []),
      hostItem("Open definition", host.openDefinition, ref),
      newer
        ? hostItem("Update to a newer version…", host.updateBlock, ref)
        : item("Update to a newer version…", undefined, { disabledReason: "Already on the latest version" }),
      "separator",
      ...orientCommands(dispatch),
      "separator",
      ...clipboardCommands(context),
      "separator",
      ...findingsCommand(context, ref, block?.findings.length ?? 0),
      hostItem("Detach…", host.detach, ref),
      item(`Delete block and ${block?.memberRefs.length ?? 0} members`, () => dispatch({ type: "DELETE_SELECTION" }), {
        shortcut: "Del",
        danger: true,
      }),
    ];
  }

  if (target.kind === "port" && target.ref) {
    const ref = target.ref;
    const found = findPort(design, ref);
    const external = found?.port.is_external ?? false;
    const instance = found ? lockingInstance(design, found.node.local_id) : undefined;
    const startRefusal = checkEndpoint(design, state.library, ref);
    const holder = design.edges?.find((edge) => edge.from_port === ref || edge.to_port === ref);
    return [
      item("Connect from here", startRefusal ? undefined : () => context.connectFrom(ref), {
        shortcut: "C",
        ...(startRefusal ? { disabledReason: startRefusal.message } : {}),
      }),
      item(
        external ? "Mark as internal" : "Mark as external interface",
        instance ? undefined : () => dispatch({ type: "SET_PORT_EXTERNAL", ref, external: !external }),
        instance ? { disabledReason: `Locked in ${instance.local_id}` } : {},
      ),
      (() => {
        const deadEnd = found?.port.dead_end ?? false;
        const reason = instance
          ? `Locked in ${instance.local_id}`
          : !deadEnd && holder
            ? `Connected by ${holder.local_id}`
            : undefined;
        return item(
          deadEnd ? "Open dead end" : "Dead-end this port",
          reason ? undefined : () => dispatch({ type: "SET_PORT_DEAD_END", ref, deadEnd: !deadEnd }),
          reason ? { disabledReason: reason } : {},
        );
      })(),
      ...(holder
        ? (() => {
            const connection = connectionAt(design, ref)!;
            const next = nextFreePort(design, state.library, ref);
            return [
              item(
                "Move connection to next port",
                next ? () => dispatch({ type: "RECONNECT_EDGE", ref: holder.local_id, end: connection.end, port: next }) : undefined,
                { shortcut: "Tab", ...(next ? {} : { disabledReason: "No other free port on this component" }) },
              ),
              item(`Select ${holder.local_id}`, () => dispatch({ type: "SELECT", refs: [holder.local_id] })),
            ];
          })()
        : []),
    ];
  }

  // The canvas itself.
  const liveBlocks = (design.group_instances ?? []).filter((instance) => !instance.detached);
  const anyExpanded = liveBlocks.some((instance) => !state.collapsed.has(instance.local_id));
  return [
    item("Select all", () => dispatch({ type: "SELECT_ALL" }), { shortcut: "⌘A" }),
    pasteCommand(context, state.clipboard !== null),
    ...(context.notes
      ? [item("Add note here", () => context.view({ kind: "add-note", ...(context.at ? { at: context.at } : {}) }))]
      : []),
    "separator",
    item(state.grid.visible ? "Hide grid" : "Show grid", () => dispatch({ type: "SET_GRID", visible: !state.grid.visible })),
    item(state.grid.snap ? "Turn off snapping" : "Turn on snapping", () => dispatch({ type: "SET_GRID", snap: !state.grid.snap })),
    item(state.annotations.labels ? "Hide labels" : "Show labels", () =>
      dispatch({ type: "SET_ANNOTATIONS", labels: !state.annotations.labels }),
    ),
    item(state.annotations.dimensions ? "Hide dimensions" : "Show dimensions", () =>
      dispatch({ type: "SET_ANNOTATIONS", dimensions: !state.annotations.dimensions }),
    ),
    item(state.annotations.fluidLines ? "Hide fluid lines" : "Show fluid lines", () =>
      dispatch({ type: "SET_ANNOTATIONS", fluidLines: !state.annotations.fluidLines }),
    ),
    item(state.annotations.airLines ? "Hide air lines" : "Show air lines", () =>
      dispatch({ type: "SET_ANNOTATIONS", airLines: !state.annotations.airLines }),
    ),
    ...(context.notes
      ? [
          item(state.annotations.notes ? "Hide notes" : "Show notes", () =>
            dispatch({ type: "SET_ANNOTATIONS", notes: !state.annotations.notes }),
          ),
        ]
      : []),
    ...(liveBlocks.length > 0
      ? [
          item(anyExpanded ? "Collapse all blocks" : "Expand all blocks", () => {
            for (const instance of liveBlocks) {
              dispatch({ type: "SET_COLLAPSED", instance: instance.local_id, collapsed: anyExpanded });
            }
          }),
        ]
      : []),
    "separator",
    item("Zoom to fit", () => context.view({ kind: "fit" }), { shortcut: "⇧1" }),
    item("Zoom to 100%", () => context.view({ kind: "zoom-reset" }), { shortcut: "0" }),
  ];
}
